import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import Image from 'next/image';
import { Menu } from '@headlessui/react';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import 'react-phone-number-input/style.css';
import { getCountries, Country } from 'react-phone-number-input/input';
//@ts-expect-error - no types available
import countryNames from 'react-phone-number-input/locale/it';
import { cn } from '@restworld/utils-common';

export function CountriesMenu({ onSelect, favorites, defaultCountry, wrapperStyle }: CountriesMenuProps) {
  const [selectedCountry, setSelectedCountry] = useState<Country>(defaultCountry);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const handleSelection = useCallback(
    (newValue: Country) => {
      setSelectedCountry(newValue);
      onSelect && onSelect(newValue);
    },
    [onSelect]
  );

  const countries = useMemo(() => filterCountries(getCountries(), favorites), [favorites]);

  return (
    <Wrapper twStyle={wrapperStyle}>
      <Menu>
        <Menu.Button
          ref={refs.setReference}
          className="inline-flex items-center p-2 h-8 w-[49px] gap-1 text-sm font-medium text-black rounded bg-neutral-40"
        >
          <span>{renderFlag(selectedCountry)}</span>
          <span>{selectedCountry}</span>
        </Menu.Button>
        <Menu.Items
          ref={refs.setFloating}
          style={floatingStyles}
          className="mt-4 max-h-[120px] md:max-h-[225px] w-[320px] max-w-[320px] overflow-y-auto rounded-2xl shadow-[0px 4px 16px 0px rgba(0, 0, 26, 0.50)] border-none bg-white"
        >
          {countries?.map((country) => (
            <Menu.Item key={country}>
              {({ active }) => (
                <StyledButton
                  isActive={active}
                  isLastFavorite={!!favorites?.includes(country) && favorites[favorites.length - 1] === country}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelection(country);
                  }}
                >
                  <span>{renderFlag(country)}</span>
                  <span>{countryNames[country]}</span>
                </StyledButton>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Menu>
    </Wrapper>
  );
}
export default CountriesMenu;

export interface CountriesMenuProps {
  onSelect?: (value: string) => void;
  favorites?: Country[];
  defaultCountry: Country;
  wrapperStyle?: string;
}

const renderFlag = (country: Country) => (
  <Image
    src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`}
    alt="flag icon"
    height={11}
    width={16}
  />
);

const filterCountries = (countries: Country[], favorites: Country[] | undefined): Country[] => {
  if (!favorites || favorites.length === 0) return countries;
  countries = countries.filter((country) => !favorites.includes(country));
  for (let i = favorites.length - 1; i >= 0; i--) {
    countries.unshift(favorites[i]);
  }

  return countries;
};

const Wrapper = ({ twStyle, className, ...props }: any) => <span className={cn(twStyle, className)} {...props} />;
const StyledButton = ({ isActive, isLastFavorite, className, ...props }: any) => (
  <button
    className={cn(
      'flex w-full items-center gap-x-1  py-2 px-4 text-left text-sm',
      isActive ? 'bg-gray-100' : 'bg-white',
      isLastFavorite ? 'border-b border-gray-200' : '',
      className
    )}
    {...props}
  />
);
